import clsx from 'clsx';
import React from 'react';

import { setShowroomSelectedTab } from 'Apollo/Store/Showroom/UserView/resolvers';
import { MediaInfoFragment } from 'generated/graphql';

import { useIsShowroomMobileVersion } from 'lib/showroom';

import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import ClearTwoToneIcon from '@material-ui/icons/ClearTwoTone';

import ShowroomBasketItem from './Item';
import { ComponentProps as BasketItemProps } from './Item/ShowroomBasketItem';
import { useStyles } from './styles';

export type BasketActionButton = {
	id: string;
	label: string;
	icon: React.ReactNode;
	onClick(): void;
};

export type ComponentProps = {
	id: string;
	title: string;
	medias: MediaInfoFragment[];
	actions: BasketActionButton[];
	elementName: string;
	menuType: BasketItemProps['menuType'];
	emptyBasketComponent: React.ReactNode;
	getItemDesktopActions: BasketItemProps['getDesktopActions'];
	getItemMobileActions: BasketItemProps['getMobileActions'];
};

const ShowroomBasketLayout = (props: ComponentProps) => {
	const { id, title, medias, actions, elementName, menuType, emptyBasketComponent } = props;
	const { getItemDesktopActions, getItemMobileActions } = props;

	const classes = useStyles();
	const mobileVersion = useIsShowroomMobileVersion();

	const basketHeader = React.useMemo(
		() => (
			<div id={`${id}_header`} className={clsx(classes.headerContainer, { [classes.headerContainerMobile]: mobileVersion })}>
				<Typography id={`${id}_title`} className={clsx(classes.title, { [classes.titleMobile]: mobileVersion })} component="h2" variant="h2">
					{title}
				</Typography>
				<div id={`${id}_actions`} className={clsx(classes.actionsContainer, { [classes.actionsContainerMobile]: mobileVersion })}>
					{!mobileVersion
						? actions.map(action => (
								<React.Fragment key={`${id}_${action.id}`}>
									<Button
										id={`${id}_${action.id}`}
										classes={{
											label: classes.actionBtnLabel
										}}
										startIcon={action.icon}
										disableRipple
										onClick={action.onClick}
									>
										{action.label}
									</Button>
									<Divider className={classes.actionDivider} orientation="vertical" flexItem />
								</React.Fragment>
						  ))
						: null}
					<IconButton
						id={`${id}_closeBasketBtn`}
						classes={{
							root: classes.closeBasketBtn
						}}
						disableRipple
						onClick={() => {
							setShowroomSelectedTab(null);
						}}
					>
						<ClearTwoToneIcon className={classes.closeBasketIcon} />
					</IconButton>
				</div>
			</div>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[actions, id, title, mobileVersion]
	);

	return (
		<div id={id} className={clsx(classes.layoutRoot, { [classes.layoutRootMobile]: mobileVersion })}>
			{basketHeader}
			<div id={`${id}_basketContent`} className={clsx(classes.basketContainer, { [classes.basketContainerMobile]: mobileVersion })}>
				{medias.length === 0 ? (
					emptyBasketComponent
				) : (
					<>
						<div id={`${id}_basketCounter`} className={classes.basketCounter}>
							{medias.length} {elementName}
						</div>
						<Grid container spacing={9}>
							{medias.map(media => (
								<ShowroomBasketItem
									id={`${id}_basketItem_${media.id}`}
									key={`${id}_basketItem_${media.id}`}
									media={media}
									menuType={menuType}
									getDesktopActions={getItemDesktopActions}
									getMobileActions={getItemMobileActions}
								/>
							))}
						</Grid>
					</>
				)}
			</div>
		</div>
	);
};

export default React.memo(ShowroomBasketLayout);
